import React, { useMemo, useState } from 'react'
import {
  Autocomplete,
  AUTOCOMPLETE_DEMO_OPTIONS,
  AUTOCOMPLETE_PREVIEW_STATE_LABELS,
  AUTOCOMPLETE_PREVIEW_STATES,
  type AutocompletePreviewState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './AutocompleteShowcase.css'

const PROVIDER_OPTIONS = [
  { value: 'ava-smith', label: 'Dr. Ava Smith' },
  { value: 'marcus-jones', label: 'Dr. Marcus Jones' },
  { value: 'priya-lee', label: 'Dr. Priya Lee' },
  { value: 'daniel-chen', label: 'Dr. Daniel Chen' },
  { value: 'sofia-patel', label: 'Dr. Sofia Patel' },
]

const AutocompleteShowcase: React.FC = () => {
  const [patientName, setPatientName] = useState('')
  const [providerName, setProviderName] = useState('')
  const [label, setLabel] = useState('Patient name')
  const [disabled, setDisabled] = useState(false)
  const [optional, setOptional] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [allowCustom, setAllowCustom] = useState(false)
  const [error, setError] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [helperText, setHelperText] = useState('')
  const [highlightedState, setHighlightedState] =
    useState<AutocompletePreviewState>('expanded')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'autocomplete-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="autocomplete-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as AutocompletePreviewState,
                  )
                }
              >
                {AUTOCOMPLETE_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {AUTOCOMPLETE_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'autocomplete-label',
            label: 'Patient name label',
            control: (
              <input
                id="autocomplete-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'autocomplete-helper-text',
            label: 'Helper text',
            control: (
              <input
                id="autocomplete-helper-text"
                className="ui-field-control"
                type="text"
                value={helperText}
                placeholder="Optional helper text"
                onChange={(event) => setHelperText(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'autocomplete-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="autocomplete-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'autocomplete-optional',
            label: 'Optional',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="autocomplete-optional"
                  type="checkbox"
                  checked={optional}
                  onChange={(event) => setOptional(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'autocomplete-tooltip',
            label: 'Tooltip',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="autocomplete-tooltip"
                  type="checkbox"
                  checked={showTooltip}
                  onChange={(event) => setShowTooltip(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'autocomplete-allow-custom',
            label: 'Allow custom',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="autocomplete-allow-custom"
                  type="checkbox"
                  checked={allowCustom}
                  onChange={(event) => setAllowCustom(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'autocomplete-error',
            label: 'Error',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="autocomplete-error"
                  type="checkbox"
                  checked={error}
                  onChange={(event) => setError(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'autocomplete-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="autocomplete-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      allowCustom,
      darkMode,
      disabled,
      error,
      helperText,
      highlightedState,
      label,
      optional,
      showTooltip,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Autocomplete"
        description="Filterable suggestions with match highlighting in the dropdown. Same field chrome as Combobox, built for predictive search."
        stageClassName="ui-preview__stage--wide autocomplete-showcase__stage-wrap"
      >
        <div
          className={`autocomplete-showcase__sheet${
            darkMode ? ' autocomplete-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Type to open filtered suggestions with match highlighting. Focus
              alone does not open the list; use the chevron to browse all
              options. Property controls apply to both examples.
            </p>
            <div className="autocomplete-showcase__live-grid">
              <Autocomplete
                label={label}
                placeholder="Type to search"
                value={patientName}
                options={AUTOCOMPLETE_DEMO_OPTIONS}
                disabled={disabled}
                optional={optional}
                showTooltip={showTooltip}
                allowCustom={allowCustom}
                darkMode={darkMode}
                error={error}
                helperText={helperText || undefined}
                onChange={setPatientName}
              />
              <Autocomplete
                label="Provider name"
                placeholder="Type to search"
                value={providerName}
                options={PROVIDER_OPTIONS}
                disabled={disabled}
                optional={optional}
                showTooltip={showTooltip}
                allowCustom={allowCustom}
                darkMode={darkMode}
                error={error}
                helperText={helperText || undefined}
                onChange={setProviderName}
              />
            </div>
          </section>

          <section className="autocomplete-showcase__matrix">
            {AUTOCOMPLETE_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`autocomplete-showcase__row${
                  previewState === highlightedState
                    ? ' autocomplete-showcase__row--selected'
                    : ''
                }${
                  previewState === 'expanded'
                    ? ' autocomplete-showcase__row--expanded'
                    : ''
                }${darkMode ? ' autocomplete-showcase__row--dark' : ''}`}
              >
                <h4 className="autocomplete-showcase__label">
                  {AUTOCOMPLETE_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <Autocomplete previewState={previewState} darkMode={darkMode} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Autocomplete"
        sections={propertySections}
      />
    </>
  )
}

export default AutocompleteShowcase
